import type { MatrixClient } from 'matrix-js-sdk'
import {
  ensureMatrixSession,
  getMatrixSession,
  refreshMatrixAccessToken,
} from '@/modules/chat-matrix-fast/matrixSession'
import { MATRIX_INITIAL_SYNC_LIMIT } from '@/modules/chat-matrix-fast/constants/chat'
import { matrixSyncDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

/** Сколько подряд 401-recovery допускаем, прежде чем открыть circuit. */
const SYNC_AUTH_RECOVERY_MAX_ATTEMPTS = 3
/** Окно для подсчёта попыток. */
const SYNC_AUTH_RECOVERY_WINDOW_MS = 60_000
/** После открытия circuit не рестартим sync до истечения cooldown. */
const SYNC_AUTH_RECOVERY_COOLDOWN_MS = 5 * 60_000
/** Пауза перед N-й попыткой recovery (сдерживает лавину startClient). */
const SYNC_AUTH_RECOVERY_BACKOFF_MS = [0, 1_500, 4_000] as const

/** Circuit-счётчики 401-recovery. Владелец состояния — matrixClientLifecycle. */
export interface MatrixSyncAuthRecoveryCircuit {
  attemptAt: number[]
  blockedUntil: number
  blockReason: string | null
}

/**
 * Явные зависимости от lifecycle-модуля: он владеет mutable-состоянием
 * (текущий client, syncStarted, circuit-счётчики) и передаёт доступ сюда
 * callback'ами — так разрывается цикл lifecycle ↔ recovery без динамических import'ов.
 */
export interface MatrixAuthRecoveryLifecycleDeps {
  circuit: MatrixSyncAuthRecoveryCircuit
  getClient: () => MatrixClient | null
  resetMatrixClient: (reason?: string) => void
  setSyncStarted: (value: boolean) => void
}

let lifecycleDeps: MatrixAuthRecoveryLifecycleDeps | null = null

/** Вызывается один раз при инициализации matrixClientLifecycle. */
export function initMatrixAuthRecovery(deps: MatrixAuthRecoveryLifecycleDeps): void {
  lifecycleDeps = deps
}

function requireLifecycleDeps(): MatrixAuthRecoveryLifecycleDeps {
  if (!lifecycleDeps) {
    throw new Error('matrixAuthRecovery: lifecycle-зависимости не инициализированы (нужен импорт matrixClientLifecycle)')
  }

  return lifecycleDeps
}

let refreshingSessionPromise: Promise<void> | null = null
let syncAuthRecoveryPromise: Promise<void> | null = null

export function getMatrixHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const matrixError = error as {
    httpStatus?: number
    statusCode?: number
    response?: { status?: number }
  }

  return matrixError.httpStatus
    ?? matrixError.statusCode
    ?? matrixError.response?.status
}

function getMatrixErrcode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const matrixError = error as { errcode?: string; name?: string }
  return matrixError.errcode || matrixError.name
}

export function isMatrixUnknownTokenError(error: unknown): boolean {
  const errcode = getMatrixErrcode(error)
  if (errcode === 'M_UNKNOWN_TOKEN' || errcode === 'M_MISSING_TOKEN') {
    return true
  }

  return getMatrixHttpStatus(error) === 401
}

export function isMatrixAuthError(error: unknown): boolean {
  return isMatrixUnknownTokenError(error)
}

async function applyRefreshedMatrixToken(session: { accessToken: string }): Promise<void> {
  const { getClient, resetMatrixClient } = requireLifecycleDeps()
  const client = getClient()

  if (client) {
    matrixSyncDebugLog('token refresh → setAccessToken (client kept)')
    client.setAccessToken(session.accessToken)
    return
  }

  matrixSyncDebugLog('token refresh → no client, resetMatrixClient')
  resetMatrixClient('token-refresh-no-client')
}

export async function refreshMatrixSessionWithLock(): Promise<void> {
  if (!refreshingSessionPromise) {
    refreshingSessionPromise = (async () => {
      try {
        matrixSyncDebugLog('refreshMatrixSessionWithLock → refresh token')
        const session = await refreshMatrixAccessToken({ force: true })
        await applyRefreshedMatrixToken(session)
      } catch (error) {
        matrixSyncDebugLog('refreshMatrixSessionWithLock → failed, force new session', {
          error: error instanceof Error ? error.message : String(error),
        }, { stack: true })
        const session = await ensureMatrixSession({ force: true })
        if (!session) {
          throw new Error('Не удалось восстановить сессию чата')
        }
        await applyRefreshedMatrixToken(session)
      }
    })().finally(() => {
      refreshingSessionPromise = null
    })
  }

  await refreshingSessionPromise
}

export async function withMatrixAuthRetry<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action()
  } catch (error) {
    if (!isMatrixUnknownTokenError(error)) {
      throw error
    }

    await refreshMatrixSessionWithLock()
    return action()
  }
}

export function isMatrixSyncAuthRecoveryBlocked(): boolean {
  return Date.now() < requireLifecycleDeps().circuit.blockedUntil
}

/** Сброс circuit breaker (после починки Synapse / смены сессии). */
export function clearMatrixSyncAuthRecoveryCircuit(reason = 'manual'): void {
  matrixSyncDebugLog('clearMatrixSyncAuthRecoveryCircuit', { reason })
  const { circuit } = requireLifecycleDeps()
  circuit.attemptAt = []
  circuit.blockedUntil = 0
  circuit.blockReason = null
}

function noteMatrixSyncAuthRecoverySuccess(): void {
  const { circuit } = requireLifecycleDeps()
  if (circuit.attemptAt.length === 0 && circuit.blockedUntil === 0) {
    return
  }
  matrixSyncDebugLog('noteMatrixSyncAuthRecoverySuccess → clear attempts')
  circuit.attemptAt = []
  circuit.blockedUntil = 0
  circuit.blockReason = null
}

function pruneMatrixSyncAuthRecoveryAttempts(now: number): void {
  const { circuit } = requireLifecycleDeps()
  circuit.attemptAt = circuit.attemptAt.filter(
    at => now - at < SYNC_AUTH_RECOVERY_WINDOW_MS,
  )
}

function openMatrixSyncAuthRecoveryCircuit(reason: string): void {
  const { circuit, getClient, setSyncStarted } = requireLifecycleDeps()
  circuit.blockedUntil = Date.now() + SYNC_AUTH_RECOVERY_COOLDOWN_MS
  circuit.blockReason = reason
  matrixSyncDebugLog('openMatrixSyncAuthRecoveryCircuit', {
    reason,
    blockedUntil: circuit.blockedUntil,
    attempts: circuit.attemptAt.length,
  }, { stack: true })

  setSyncStarted(false)
  const client = getClient()
  if (client) {
    try {
      client.stopClient()
    } catch {
      // already stopped
    }
  }
}

function beginMatrixSyncAuthRecoveryAttempt(): number {
  const now = Date.now()
  pruneMatrixSyncAuthRecoveryAttempts(now)
  const { circuit } = requireLifecycleDeps()
  circuit.attemptAt.push(now)
  return circuit.attemptAt.length
}

async function waitMatrixSyncAuthRecoveryBackoff(attemptNumber: number): Promise<void> {
  const delayMs = SYNC_AUTH_RECOVERY_BACKOFF_MS[
    Math.min(attemptNumber - 1, SYNC_AUTH_RECOVERY_BACKOFF_MS.length - 1)
  ] ?? 0
  if (delayMs <= 0) {
    return
  }
  matrixSyncDebugLog('recoverMatrixSyncAfterAuthError → backoff', {
    attemptNumber,
    delayMs,
  })
  await new Promise<void>(resolve => {
    window.setTimeout(resolve, delayMs)
  })
}

export function isMatrixSyncAuthFailure(error: unknown): boolean {
  const matrixError = error as {
    httpStatus?: number
    statusCode?: number
    errcode?: string
    name?: string
  } | null | undefined

  const status = matrixError?.httpStatus
    ?? matrixError?.statusCode
    ?? getMatrixHttpStatus(error)
  const errcode = matrixError?.errcode || matrixError?.name

  return status === 401
    || status === 403
    || errcode === 'M_UNKNOWN_TOKEN'
    || errcode === 'M_MISSING_TOKEN'
}

export function readMatrixSyncError(matrixClient: MatrixClient): unknown {
  const syncData = matrixClient.getSyncStateData?.() as {
    error?: unknown
  } | null | undefined

  return syncData?.error
}

export async function recoverMatrixSyncAfterAuthError(
  matrixClient: MatrixClient,
  reason: string,
): Promise<void> {
  const { circuit, setSyncStarted } = requireLifecycleDeps()

  if (isMatrixSyncAuthRecoveryBlocked()) {
    matrixSyncDebugLog('recoverMatrixSyncAfterAuthError → blocked by circuit', {
      reason,
      blockReason: circuit.blockReason,
      blockedUntil: circuit.blockedUntil,
    })
    return
  }

  if (syncAuthRecoveryPromise) {
    await syncAuthRecoveryPromise
    return
  }

  syncAuthRecoveryPromise = (async () => {
    if (isMatrixSyncAuthRecoveryBlocked()) {
      return
    }

    const attemptNumber = beginMatrixSyncAuthRecoveryAttempt()
    matrixSyncDebugLog('recoverMatrixSyncAfterAuthError → start', {
      reason,
      attemptNumber,
    })

    if (attemptNumber > SYNC_AUTH_RECOVERY_MAX_ATTEMPTS) {
      openMatrixSyncAuthRecoveryCircuit(
        `homeserver отклоняет Matrix-токен (401). Проверьте MATRIX_HOMESERVER / SYNAPSE_ADMIN_TOKEN в namespace. (${reason})`,
      )
      return
    }

    try {
      await waitMatrixSyncAuthRecoveryBackoff(attemptNumber)
      if (isMatrixSyncAuthRecoveryBlocked()) {
        return
      }

      await refreshMatrixSessionWithLock()
      const session = getMatrixSession()
      if (session?.accessToken) {
        matrixClient.setAccessToken(session.accessToken)
      }

      if (matrixClient.getSyncState() === 'ERROR' || !matrixClient.clientRunning) {
        setSyncStarted(false)
        try {
          matrixClient.stopClient()
        } catch {
          // already stopped
        }
        if (isMatrixSyncAuthRecoveryBlocked()) {
          return
        }
        setSyncStarted(true)
        await matrixClient.startClient({ initialSyncLimit: MATRIX_INITIAL_SYNC_LIMIT })
      }

      matrixSyncDebugLog('recoverMatrixSyncAfterAuthError → done', {
        syncState: matrixClient.getSyncState(),
        clientRunning: matrixClient.clientRunning,
        attemptNumber,
      })

      // Если sync сразу снова ERROR — следующий sync-event доберёт счётчик.
      // Успех (PREPARED/SYNCING) сбросит attempts в attachMatrixSyncAuthRecovery.
      if (
        attemptNumber >= SYNC_AUTH_RECOVERY_MAX_ATTEMPTS
        && matrixClient.getSyncState() === 'ERROR'
        && isMatrixSyncAuthFailure(readMatrixSyncError(matrixClient))
      ) {
        openMatrixSyncAuthRecoveryCircuit(
          `homeserver отклоняет Matrix-токен (401) после ${attemptNumber} попыток. (${reason})`,
        )
      }
    } catch (error) {
      matrixSyncDebugLog('recoverMatrixSyncAfterAuthError → failed', {
        error: error instanceof Error ? error.message : String(error),
        attemptNumber,
      }, { stack: true })

      setSyncStarted(false)
      try {
        matrixClient.stopClient()
      } catch {
        // already stopped
      }

      if (attemptNumber >= SYNC_AUTH_RECOVERY_MAX_ATTEMPTS) {
        openMatrixSyncAuthRecoveryCircuit(
          `не удалось восстановить Matrix-сессию после ${attemptNumber} попыток. (${reason})`,
        )
      }
      // Без reset+startMatrixClient: это и разгоняло лавину при стабильном 401.
    }
  })().finally(() => {
    syncAuthRecoveryPromise = null
  })

  await syncAuthRecoveryPromise
}

export function attachMatrixSyncAuthRecovery(matrixClient: MatrixClient): void {
  const debugClient = matrixClient as MatrixClient & {
    __syncAuthRecoveryAttached?: boolean
  }

  if (debugClient.__syncAuthRecoveryAttached) {
    return
  }

  debugClient.__syncAuthRecoveryAttached = true

  matrixClient.on('sync' as any, (state: string) => {
    if (state === 'PREPARED' || state === 'SYNCING') {
      noteMatrixSyncAuthRecoverySuccess()
      return
    }

    if (state !== 'ERROR') {
      return
    }

    if (isMatrixSyncAuthRecoveryBlocked()) {
      matrixSyncDebugLog('sync ERROR ignored — auth recovery circuit open', {
        blockReason: requireLifecycleDeps().circuit.blockReason,
      })
      return
    }

    const syncError = readMatrixSyncError(matrixClient)
    if (!isMatrixSyncAuthFailure(syncError)) {
      return
    }

    void recoverMatrixSyncAfterAuthError(matrixClient, 'sync-error')
  })
}
